import React, { useState, useEffect } from 'react';
import Input from '../Common/Input';
import Select from '../Common/Select';
import Button from '../Common/Button';
import api from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { showSuccess, showError } from '../../utils/swal';

const SaleForm = ({ portfolio, onClose, onSuccess }) => {
  const { user } = useAuth();
  const isManager = user?.is_manager || user?.role === 'office_owner';
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]);

  const [formData, setFormData] = useState({
    portfolio_id: portfolio?.id || '',
    customer_id: portfolio?.option_type === 'customer' && portfolio?.option_customer_id
      ? portfolio.option_customer_id : '',
    user_id: isManager ? '' : (user?.id || ''),
    sale_date: new Date().toISOString().split('T')[0],
    contract_date: '',
    purchase_motive: '',
    list_price: portfolio?.list_price || '',
    final_sale_price: portfolio?.list_price || '',
    commission_rate: portfolio?.commission_rate || '',
    commission_amount: portfolio?.commission_rate && portfolio?.list_price
      ? (parseFloat(portfolio.list_price) * parseFloat(portfolio.commission_rate) / 100).toFixed(2)
      : '',
    commission_payment_date: '',
    sale_notes: '',
  });

  useEffect(() => {
    fetchUsers();
    fetchCustomers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Kullanıcılar yüklenemedi:', error);
      setUsers([]);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/customers');
      setCustomers(Array.isArray(response.data.data) ? response.data.data : []);
      console.log(response.data);
    } catch (error) {
      console.error('Müşteriler yüklenemedi:', error);
      setCustomers([]);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Komisyon oranı değiştiğinde tutarı hesapla
    if (name === 'commission_rate') {
      const salePrice = parseFloat(formData.final_sale_price) || 0;
      const rate = parseFloat(value) || 0;
      setFormData(prev => ({
        ...prev,
        commission_rate: value,
        commission_amount: salePrice > 0 ? (salePrice * rate / 100).toFixed(2) : '',
      }));
      return;
    }
    
    // Komisyon tutarı değiştiğinde oranı hesapla
    if (name === 'commission_amount') {
      const salePrice = parseFloat(formData.final_sale_price) || 0;
      const amount = parseFloat(value) || 0;
      setFormData(prev => ({
        ...prev,
        commission_amount: value,
        commission_rate: salePrice > 0 ? ((amount / salePrice) * 100).toFixed(2) : '',
      }));
      return;
    }

    // Satış fiyatı değiştiğinde komisyon tutarını yeniden hesapla
    if (name === 'final_sale_price' && formData.commission_rate) {
      const rate = parseFloat(formData.commission_rate) || 0;
      const salePrice = parseFloat(value) || 0;
      setFormData(prev => ({
        ...prev,
        final_sale_price: value,
        commission_amount: salePrice > 0 ? (salePrice * rate / 100).toFixed(2) : '',
      }));
      return;
    }

    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.post('/sales', formData);
      showSuccess('Satış başarıyla kaydedildi');
      if (onSuccess) onSuccess();
      if (onClose) onClose();
    } catch (error) {
      console.error('Satış kaydedilemedi:', error);
      if (error.response?.data?.errors) {
        const errors = Object.values(error.response.data.errors).flat();
        showError(errors.join('\n'));
      } else {
        showError('Satış kaydedilirken bir hata oluştu');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
        <h3 className="font-semibold text-blue-900 mb-1">Satılacak Gayrimenkul</h3>
        <p className="text-sm text-blue-800">{portfolio?.title}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {isManager ? (
          <Select
            label="Satışı Yapan Görevli"
            name="user_id"
            value={formData.user_id}
            onChange={handleChange}
            options={users.map(u => ({
              value: u.id,
              label: u.name
            }))}
            required
          />
        ) : (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Satışı Yapan Görevli</label>
            <p className="px-3 py-2 bg-gray-100 border border-gray-200 rounded-md text-gray-700">{user?.name}</p>
          </div>
        )}

        <Select
          label="Müşteri"
          name="customer_id"
          value={formData.customer_id}
          onChange={handleChange}
          options={customers.map(customer => ({
            value: customer.id,
            label: customer.name + ' ' + customer.surname
          }))}
          required
        />

        <Input
          label="Satış Tarihi"
          name="sale_date"
          type="date"
          value={formData.sale_date}
          onChange={handleChange}
          required
        />

        <Input
          label="Sözleşme Tarihi"
          name="contract_date"
          type="date"
          value={formData.contract_date}
          onChange={handleChange}
        />

        <Select
          label="Satın Alma Tercihi"
          name="purchase_motive"
          value={formData.purchase_motive}
          onChange={handleChange}
          options={[
            { value: 'investment', label: 'Yatırım' },
            { value: 'residence', label: 'Oturum' },
            { value: 'investment_and_residence', label: 'Yatırım ve Oturum' },
          ]}
        />

        <Input
          label="Liste Fiyatı"
          name="list_price"
          type="number"
          step="0.01"
          value={formData.list_price}
          onChange={handleChange}
          required
        />

        <Input
          label="Satış Fiyatı"
          name="final_sale_price"
          type="number"
          step="0.01"
          value={formData.final_sale_price}
          onChange={handleChange}
          required
        />

        <Input
          label="Komisyon Oranı (%)"
          name="commission_rate"
          type="number"
          step="0.01"
          min="0"
          max="100"
          value={formData.commission_rate}
          onChange={handleChange}
        />

        <Input
          label="Komisyon Tutarı (₺)"
          name="commission_amount"
          type="number"
          step="0.01"
          min="0"
          value={formData.commission_amount}
          onChange={handleChange}
        />

        <Input
          label="Komisyon Ödeme Tarihi"
          name="commission_payment_date"
          type="date"
          value={formData.commission_payment_date}
          onChange={handleChange}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Satış Notları
        </label>
        <textarea
          name="sale_notes"
          value={formData.sale_notes}
          onChange={handleChange}
          rows="3"
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          placeholder="Satış ile ilgili notlar..."
        />
      </div>

      <div className="flex gap-2 justify-end pt-4 border-t">
        <Button
          type="button"
          variant="secondary"
          onClick={onClose}
        >
          İptal
        </Button>
        <Button
          type="submit"
          disabled={loading}
        >
          {loading ? 'Kaydediliyor...' : 'Satışı Kaydet'}
        </Button>
      </div>
    </form>
  );
};

export default SaleForm;
